import { Link } from "@tanstack/react-router";
import { CircleCheckBig } from "lucide-react";

import { MapPreviewImage } from "#/components/MapPreviewImage";
import { Button } from "#/components/ui/button";
import { Card } from "#/components/ui/card";
import { cn } from "#/lib/utils";

import { formatBestScoreLine } from "./-play.labels";
import { CALENDAR_PREVIEW_BOX_PX } from "./-play.previewSizing";

type DayVariantCellProps = {
  variantLabel: "STANDARD" | "PREMIUM";
  mapId?: string;
  bestDays: number | null;
  completed: boolean;
  className?: string;
};

export function DayVariantCell({
  variantLabel,
  mapId,
  bestDays,
  completed,
  className,
}: DayVariantCellProps) {
  return (
    <Card
      className={cn(
        "min-w-0 flex-row items-center gap-3 overflow-hidden bg-card px-3 py-3 ring-0 border-2 border-dashed",
        completed && "bg-accent/25 border-solid border-accent/50 dark:bg-accent/10 ",
        className,
      )}
    >
      <DayVariantCellCopy variantLabel={variantLabel} bestDays={bestDays} mapId={mapId} />
      {mapId ? <DayVariantMapThumb mapId={mapId} completed={completed} /> : null}
    </Card>
  );
}

function DayVariantCellCopy({
  variantLabel,
  bestDays,
  mapId,
}: {
  variantLabel: "STANDARD" | "PREMIUM";
  bestDays: number | null;
  mapId?: string;
}) {
  return (
    <div className="flex min-w-0 grow flex-col justify-between">
      <div>
        <p
          className={cn(
            "text-xs font-semibold uppercase tracking-[0.14em]",
            bestDays === null && "text-muted-foreground",
          )}
        >
          {variantLabel}
        </p>
        <p className={cn("mt-2 text-sm", bestDays === null && "text-muted-foreground")}>
          {formatBestScoreLine(bestDays)}
        </p>
      </div>
      {mapId ? <DayVariantActions mapId={mapId} /> : null}
    </div>
  );
}

function DayVariantActions({ mapId }: { mapId: string }) {
  return (
    <div className="mt-3 flex gap-2">
      <Link to="/play/$mapId" params={{ mapId }} preload={false} className="w-24">
        <Button size="sm" className="w-full">
          Play
        </Button>
      </Link>
      <Link to="/leaderboard/$mapId" params={{ mapId }} preload={false} className="w-24">
        <Button size="sm" variant="accent" className="w-full">
          Leaderboard
        </Button>
      </Link>
    </div>
  );
}

function DayVariantMapThumb({ mapId, completed }: { mapId: string; completed: boolean }) {
  return (
    <MapPreviewImage mapId={mapId} maxSize={CALENDAR_PREVIEW_BOX_PX} className="group rounded-sm">
      {completed ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-sm bg-foreground/20 backdrop-blur-[2px] transition-opacity duration-150 ease-out group-hover:opacity-0">
          <CircleCheckBig className="text-background" size={28} strokeWidth={2.25} />
        </div>
      ) : null}
    </MapPreviewImage>
  );
}
